import { useCallback } from "react";
import { useHttpAdapter } from "./useHttpAdapter";
import {
  AddKnowledgeSourceResponse,
  SeoOpportunity,
  SeoOpportunityError,
  SeoOpportunityResult,
  SeoRefreshErrorDetail,
} from "../types/strategy";
import { getApiErrorMessage } from "../utils/api-error";

const WEBSITE_INGEST_TIMEOUT_MS = 180000;

const getWebsiteIngestErrorMessage = (error: any, fallback: string) => {
  if (error?.code === "ECONNABORTED") {
    return "Website ingestion took too long. Try a smaller page, re-add the URL, or upload a PDF/source document instead.";
  }
  return getApiErrorMessage(error, fallback);
};

const useStrategyApi = () => {
  const { post, get, del } = useHttpAdapter();

  const buildSeoOpportunityError = useCallback(
    (error: unknown): SeoOpportunityError => {
      const fallbackMessage = "Failed to generate SEO opportunities.";
      const candidate = error as {
        message?: string;
        response?: {
          data?: {
            code?: string;
            detail?: string | SeoRefreshErrorDetail;
            message?: string;
            next_refresh_at?: string;
            days_remaining?: number;
          };
        };
      };
      const responseData = candidate?.response?.data;
      const detail = responseData?.detail;
      const detailObject =
        detail && typeof detail === "object" ? detail : undefined;
      const message =
        detailObject?.message ||
        (typeof detail === "string" ? detail : undefined) ||
        responseData?.message ||
        getApiErrorMessage(error, fallbackMessage);

      return {
        status: "error",
        message,
        error: {
          ...(detailObject || {}),
          code: detailObject?.code || responseData?.code,
          next_refresh_at:
            detailObject?.next_refresh_at || responseData?.next_refresh_at,
          days_remaining:
            detailObject?.days_remaining ?? responseData?.days_remaining,
          message,
        },
      };
    },
    []
  );

  const addKnowledgeSource = useCallback(
    async (
      source: string,
      brandId: string,
      sourceName: string
    ): Promise<AddKnowledgeSourceResponse> => {
      try {
        const payload = {
          brand_id: brandId,
          url: source,
          name: sourceName,
        };

        const response = await post<AddKnowledgeSourceResponse>(
          "/ingest/website",
          payload,
          { timeout: WEBSITE_INGEST_TIMEOUT_MS }
        );

        return response.data;
      } catch (e: any) {
        return {
          status: "error",
          message: getWebsiteIngestErrorMessage(
            e,
            "Failed to add knowledge source."
          ),
          current_sources: [] as string[],
          detectedTopics: [] as string[],
          contentOpportunity: "",
          toneConsistency: "",
          productSummary: "",
          productSummaryUpdated: false,
        };
      }
    },
    [post]
  );

  const triggerWebsiteContextIngestion = useCallback(
    async (
      brandId: string,
      url: string,
      sourceName: string
    ): Promise<AddKnowledgeSourceResponse> => {
      try {
        const response = await post<AddKnowledgeSourceResponse>(
          "/ingest/website",
          {
            brand_id: brandId,
            url,
            name: sourceName,
          },
          { timeout: WEBSITE_INGEST_TIMEOUT_MS }
        );

        return response.data;
      } catch (e: any) {
        return {
          status: "error",
          message: getWebsiteIngestErrorMessage(
            e,
            "Failed to start website context processing."
          ),
          current_sources: [] as string[],
          detectedTopics: [] as string[],
          contentOpportunity: "",
          toneConsistency: "",
          productSummary: "",
          productSummaryUpdated: false,
        };
      }
    },
    [post]
  );

  const ingestFile = useCallback(
    async (
      file: File,
      brandId: string
    ): Promise<AddKnowledgeSourceResponse> => {
      try {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("brand_id", brandId);

        const response = await post<AddKnowledgeSourceResponse>(
          "/ingest/file",
          formData,
          {
            headers: {
              "Content-Type": "multipart/form-data",
            },
          }
        );

        return response.data;
      } catch (e: any) {
        return {
          status: "error",
          message: getApiErrorMessage(e, "Failed to upload file."),
          current_sources: [] as string[],
          detectedTopics: [] as string[],
          contentOpportunity: "",
          toneConsistency: "",
          productSummary: "",
          productSummaryUpdated: false,
        };
      }
    },
    [post]
  );

  const generateSeoOpportunities = useCallback(
    async (
      productId: string,
      refresh: boolean
    ): Promise<SeoOpportunityResult | null> => {
      try {
        const type = refresh ? "refresh" : "latest";

        const response = await get<SeoOpportunity>(
          `/seo/insights/${type}/${productId}`,
          {}
        );
        return response.data;
      } catch (e) {
        return buildSeoOpportunityError(e);
      }
    },
    [buildSeoOpportunityError, get]
  );

  const deleteKnowledgeSource = useCallback(
    async (
      brandId: string,
      sourceName: string
    ): Promise<{ status: string; message: string }> => {
      try {
        const response = await del<{ status: string; message: string }>(
          "/ingest/knowledge-source",
          { data: { brand_id: brandId, source_name: sourceName } }
        );
        return response.data;
      } catch (e: any) {
        return {
          status: "error",
          message: getApiErrorMessage(e, "Failed to remove source."),
        };
      }
    },
    [del]
  );

  return {
    addKnowledgeSource,
    triggerWebsiteContextIngestion,
    ingestFile,
    generateSeoOpportunities,
    deleteKnowledgeSource,
  };
};

export default useStrategyApi;
